import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User as UserIcon, Users, LayoutDashboard, Contact, Mic, Network, Rocket, Menu, X, AlertTriangle, Mail, Inbox, FileText, Languages } from "lucide-react";
import { setLang } from "@/i18n";

const roleStyles = {
  Admin: "bg-[#193962] text-white",
  Manager: "bg-amber-500 text-white",
  Employee: "bg-zinc-200 text-zinc-700",
  Accountant: "bg-amber-100 text-amber-800",
};

export const RoleBadge = ({ role }) => (
  <span
    data-testid="role-badge"
    className={`rounded-full text-xs px-2 py-0.5 font-[Manrope] font-medium ${roleStyles[role] || roleStyles.Employee}`}
  >
    {role}
  </span>
);

export default function Navbar() {
  const { user, setUser, logout } = useAuth();
  const changeLang = async (k) => {
    setLang(k);
    try { const r = await api.put("/profile/language", { lang: k }); setUser(r.data); } catch (e) { /* keep local choice */ }
  };
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [synergyCount, setSynergyCount] = useState(0);
  const [svcStatus, setSvcStatus] = useState({ has_issues: false, incidents: [] });
  const initials = user?.name?.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  useEffect(() => {
    if (user?.role === "Accountant") return undefined;
    let active = true;
    const fetchCount = () => api.get("/synergy/unseen-count").then((r) => { if (active) setSynergyCount(r.data.count || 0); }).catch(() => {});
    const fetchSvc = () => api.get("/service-status").then((r) => { if (active) setSvcStatus(r.data); }).catch(() => {});
    fetchCount();
    fetchSvc();
    const id = setInterval(() => { fetchCount(); fetchSvc(); }, 60000);
    return () => { active = false; clearInterval(id); };
  }, [location.pathname, user?.role]);

  const links = user?.role === "Accountant" ? [
    { to: "/invoices", label: "Faktury", Icon: FileText, testid: "nav-invoices" },
    { to: "/accounting/clients", label: "Kontrahenci", Icon: Contact, testid: "nav-accounting-clients" },
  ] : [
    { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/clients", label: "Clients", Icon: Contact, testid: "nav-clients" },
    { to: "/meetings", label: "Meetings", Icon: Mic, testid: "nav-meetings" },
    { to: "/synergy", label: "Synergy", Icon: Network, testid: "nav-synergy", badge: synergyCount },
    { to: "/wingman", label: "Wingman", Icon: Rocket, testid: "nav-wingman" },
    ...(user?.role === "Admin" || user?.role === "Manager" ? [{ to: "/leads", label: "Leads", Icon: Inbox, testid: "nav-leads" }, { to: "/invoices", label: "Faktury", Icon: FileText, testid: "nav-invoices" }, { to: "/campaigns", label: "Kampanie", Icon: Mail, testid: "nav-campaigns" }] : []),
    ...(user?.role === "Admin" ? [{ to: "/admin/users", label: "Users", Icon: Users, testid: "nav-users" }] : []),
  ];

  const NavLink = ({ to, label, Icon, testid, badge, onClick }) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testid}
        onClick={onClick}
        className={`flex items-center gap-1.5 text-sm font-[Manrope] px-2 xl:px-3 py-2 rounded-md whitespace-nowrap transition-colors ${
          active ? "bg-zinc-100 text-zinc-900" : "text-zinc-500 hover:text-zinc-900"
        }`}
      >
        <Icon className="h-4 w-4" />
        {label}
        {badge > 0 && (
          <span data-testid="synergy-badge" className="ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#C99734] text-white text-[10px] font-[Manrope] font-semibold">{badge}</span>
        )}
      </Link>
    );
  };

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-white/80 backdrop-blur-xl">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 xl:gap-8 min-w-0">
          <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0" data-testid="brand-logo">
            <img src="/toren-mark.png" alt="Toren PATH" className="h-9 w-9 object-contain" />
            <span className="font-[Outfit] font-semibold tracking-tight hidden sm:inline whitespace-nowrap">Toren <span className="text-[#C99734]">PATH</span> CRM</span>
          </Link>
          <div className="hidden md:flex items-center gap-0.5">
            {links.map((l) => <NavLink key={l.to} {...l} />)}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {svcStatus.has_issues && (
            <div className="relative group" data-testid="service-status-indicator">
              <button className="h-9 w-9 flex items-center justify-center rounded-md text-amber-600 hover:bg-amber-50 transition-colors" aria-label="Problemy z usługami dostawców">
                <AlertTriangle className="h-5 w-5" />
              </button>
              <div className="absolute right-0 top-full mt-1 w-72 rounded-md border border-border bg-white shadow-lg p-3 z-50 hidden group-hover:block" data-testid="service-status-panel">
                <p className="text-xs font-[Manrope] font-semibold text-amber-700 mb-2">Problemy z usługami zewnętrznymi</p>
                <p className="text-[11px] text-muted-foreground font-[Manrope] mb-2">Część danych może być niedostępna z powodu problemu u podwykonawcy:</p>
                <ul className="space-y-1.5">
                  {svcStatus.incidents.map((it) => (
                    <li key={it.service} data-testid={`svc-incident-${it.service}`} className="text-xs font-[Manrope]">
                      <span className="font-medium text-zinc-800">{it.service_label}</span>
                      <span className="text-zinc-500"> — {it.reason_label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 outline-none" data-testid="user-menu-trigger">
                <span className="hidden 2xl:inline"><RoleBadge role={user?.role} /></span>
                <Avatar className="h-9 w-9 border border-border">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-zinc-100 text-zinc-700 text-sm">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white">
              <div className="px-2 py-2">
                <p className="text-sm font-[Manrope] font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground 2xl:hidden mt-0.5" data-testid="menu-role">{user?.role}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profile" data-testid="menu-profile" className="cursor-pointer">
                  <UserIcon className="h-4 w-4 mr-2" /> Profile & Region
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 flex items-center gap-1.5" data-testid="menu-language" data-no-i18n>
                <Languages className="h-4 w-4 text-zinc-500" />
                {[["pl", "Polski"], ["en", "English"]].map(([k, l]) => (
                  <button key={k} onClick={() => changeLang(k)} data-testid={`menu-lang-${k}`}
                    className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${user?.ui_lang === k ? "bg-[#193962] text-white border-[#193962]" : "border-border hover:bg-zinc-50"}`}>{l}</button>
                ))}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="cursor-pointer text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            onClick={() => setMobileOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="md:hidden h-9 w-9 flex items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 transition-colors"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-white px-4 py-3 space-y-1" data-testid="mobile-menu-panel">
          {links.map((l) => <NavLink key={l.to} {...l} onClick={() => setMobileOpen(false)} />)}
        </div>
      )}
    </nav>
  );
}
